import React, { useEffect, useState } from 'react';
import { Empty, Space, Spin, Table, Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import FeeScheduleService, {
  FeeScheduleHistoryResponse,
} from '../../service/fee-schedule-service/Fee-schedule.service';

const { Title, Text } = Typography;

// Define a local formatNumber function since the imported one doesn't exist
const formatNumber = (value: number) => {
  return value.toLocaleString('vi-VN');
};

interface FeeScheduleHistoryProps {
  posDeviceId: number;
  cardTypeId: number;
  cardTypeName: string;
  bankId?: number | null;
  bankName?: string;
}

const FeeScheduleHistory = ({
  posDeviceId,
  cardTypeId,
  cardTypeName,
  bankId,
  bankName,
}: FeeScheduleHistoryProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dataList, setDataList] = useState<FeeScheduleHistoryResponse[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Use the existing method that we know works
        const response = await FeeScheduleService.getFeeScheduleHistoryDetails(
          posDeviceId,
          cardTypeId
        );

        // Filter the results based on bankId
        let filteredData = response;
        if (bankId) {
          // Nếu bankId có giá trị, chỉ lấy lịch sử có bank.id === bankId
          filteredData = response.filter(
            (item: any) => item.bank && item.bank.id === bankId
          );
        } else {
          // Nếu không có bankId, chỉ lấy lịch sử không có bank hoặc bank.id null/undefined
          filteredData = response.filter(
            (item: any) => !item.bank || item.bank.id == null
          );
        }

        setDataList(filteredData);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cardTypeId, posDeviceId, bankId]);

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 70,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Loại thẻ',
      key: 'cardType',
      width: 150,
      render: () => cardTypeName,
    },
    {
      title: 'Ngân hàng',
      key: 'bank',
      width: 150,
      render: () => bankName || '-',
    },
    {
      title: 'Phí',
      dataIndex: 'feeRate',
      key: 'feeRate',
      width: 100,
      render: (feeRate: number) => (
        <Space>
          <Typography.Text>{formatNumber(feeRate)}</Typography.Text>
          <Typography.Text>%</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      render: (action: string) => {
        let color = 'blue';
        let text = action;

        if (action === 'CREATE') {
          color = 'green';
          text = 'Tạo mới';
        } else if (action === 'UPDATE') {
          color = 'orange';
          text = 'Cập nhật';
        } else if (action === 'DELETE') {
          color = 'red';
          text = 'Xóa';
        } else if (action === 'ACTIVATE') {
          color = 'green';
          text = 'Kích hoạt';
        } else if (action === 'DEACTIVATE') {
          color = 'volcano';
          text = 'Vô hiệu hóa';
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Thời gian',
      dataIndex: 'changedAt',
      key: 'changedAt',
      width: 200,
      render: (changedAt: string) =>
        dayjs(changedAt).format('DD/MM/YYYY HH:mm:ss'),
    },
  ];

  return (
    <div className="fee-history-container">
      <Title level={4}>Lịch sử biểu phí</Title>

      <div className="mb-4">
        <div>
          <Text strong>Loại thẻ:</Text> {cardTypeName}
        </div>
        {bankName && (
          <div>
            <Text strong>Ngân hàng:</Text> {bankName}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center my-6">
          <Spin size="large" />
        </div>
      ) : dataList.length > 0 ? (
        <Table
          columns={columns}
          dataSource={dataList}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{ y: 400 }}
        />
      ) : (
        <Empty description="Không có dữ liệu lịch sử" />
      )}
    </div>
  );
};

export default FeeScheduleHistory;
